/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  Directive,
  ElementRef,
  forwardRef,
  Host,
  Input,
  NgZone,
  Optional,
  OnDestroy,
  ViewContainerRef,
  Inject,
  ChangeDetectorRef,
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {DOCUMENT} from '@angular/platform-browser';
import {Overlay, OverlayRef, OverlayState, TemplatePortal} from '../core';
import {MdAutocomplete} from './autocomplete';
import {PositionStrategy} from '../core/overlay/position/position-strategy';
import {ConnectedPositionStrategy} from '../core/overlay/position/connected-position-strategy';
import {Observable} from 'rxjs/Observable';
import {MdOptionSelectionChange, MdOption} from '../core/option/option';
import {ENTER, UP_ARROW, DOWN_ARROW, ESCAPE} from '../core/keyboard/keycodes';
import {Dir} from '../core/rtl/dir';
import {MdInputContainer} from '../input/input-container';
import {Subscription} from 'rxjs/Subscription';
import'rxjs/add/observable/merge';
import'rxjs/add/observable/fromEvent';
import'rxjs/add/operator/filter';
import'rxjs/add/operator/switchMap';
/**
 * The height of each autocomplete option.
 */
export const AUTOCOMPLETE_OPTION_HEIGHT = 48;
/**
 * The total height of the autocomplete panel.
 */
export const AUTOCOMPLETE_PANEL_HEIGHT = 256;
/**
 * Provider that allows the autocomplete to register as a ControlValueAccessor.
 * \@docs-private
 */
export const MD_AUTOCOMPLETE_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MdAutocompleteTrigger),
  multi: true
};
/**
 * Creates an error to be thrown when attempting to use an autocomplete trigger without a panel.
 * @return {?}
 */
export function getMdAutocompleteMissingPanelError(): Error {
  return Error('Attempting to open an undefined instance of `md-autocomplete`. ' +
                   'Make sure that the id passed to the `mdAutocomplete` is correct and that ' +
                   'you\'re attempting to open it after the ngAfterContentInit hook.');
}
export class MdAutocompleteTrigger implements ControlValueAccessor, OnDestroy {
private _overlayRef: OverlayRef;
private _portal: TemplatePortal;
private _panelOpen: boolean = false;
/**
 * The subscription to positioning changes in the autocomplete panel.
 */
private _panelPositionSubscription: Subscription;
/**
 * Strategy that is used to position the panel.
 */
private _positionStrategy: ConnectedPositionStrategy;
/**
 * Whether or not the placeholder state is being overridden.
 */
private _manuallyFloatingPlaceholder = false;
/**
 * View -> model callback called when value changes
 */
_onChange: (value: any) => void = () => {};
/**
 * View -> model callback called when autocomplete has been touched
 */
_onTouched = () => {};

  /* The autocomplete panel to be attached to this trigger. */
   autocomplete: MdAutocomplete;
/**
 * Property with mat- prefix for no-conflict mode.
 * @return {?}
 */
get _matAutocomplete(): MdAutocomplete {
    return this.autocomplete;
  }
/**
 * @param {?} autocomplete
 * @return {?}
 */
set _matAutocomplete(autocomplete: MdAutocomplete) {
    this.autocomplete = autocomplete;
  }
/**
 * @param {?} _element
 * @param {?} _overlay
 * @param {?} _viewContainerRef
 * @param {?} _changeDetectorRef
 * @param {?} _dir
 * @param {?} _zone
 * @param {?} _inputContainer
 * @param {?} _document
 */
constructor(private _element: ElementRef,
private _overlay: Overlay,
private _viewContainerRef: ViewContainerRef,
private _changeDetectorRef: ChangeDetectorRef,
private _dir: Dir,
private _zone: NgZone,
private _inputContainer: MdInputContainer,
private _document: any) {}
/**
 * @return {?}
 */
ngOnDestroy() {
    if (this._panelPositionSubscription) {
      this._panelPositionSubscription.unsubscribe();
    }

    this._destroyPanel();
  }
/**
 * @return {?}
 */
get panelOpen(): boolean {
    return this._panelOpen && this.autocomplete.showPanel;
  }
/**
 * Opens the autocomplete suggestion panel.
 * @return {?}
 */
openPanel(): void {
    if (!this.autocomplete) {
      throw getMdAutocompleteMissingPanelError();
    }

    if (!this._overlayRef) {
      this._createOverlay();
    } else {
      /** Update the panel width, in case the host width has changed */
      this._overlayRef.getState().width = this._getHostWidth();
      this._overlayRef.updateSize();
    }

    if (!this._overlayRef.hasAttached()) {
      this._overlayRef.attach(this._portal);
      this._subscribeToClosingActions();
    }

    this.autocomplete._setVisibility();
    this._floatPlaceholder();
    this._panelOpen = true;
  }
/**
 * Closes the autocomplete suggestion panel.
 * @return {?}
 */
closePanel(): void {
    if (this._overlayRef && this._overlayRef.hasAttached()) {
      this._overlayRef.detach();
    }

    this._panelOpen = false;
    this._resetPlaceholder();

    // We need to trigger change detection manually, because
    // `fromEvent` doesn't seem to do it at the proper time.
    // This ensures that the placeholder is reset when the
    // user clicks outside.
    this._changeDetectorRef.detectChanges();
  }
/**
 * A stream of actions that should close the autocomplete panel, including
 * when an option is selected, on blur, and when TAB is pressed.
 * @return {?}
 */
get panelClosingActions(): Observable<MdOptionSelectionChange> {
    return Observable.merge(
      this.optionSelections,
      this.autocomplete._keyManager.tabOut,
      this._outsideClickStream
    );
  }
/**
 * Stream of autocomplete option selections.
 * @return {?}
 */
get optionSelections(): Observable<MdOptionSelectionChange> {
    return Observable.merge(...this.autocomplete.options.map(option => option.onSelectionChange));
  }
/**
 * The currently active option, coerced to MdOption type.
 * @return {?}
 */
get activeOption(): MdOption {
    if (this.autocomplete && this.autocomplete._keyManager) {
      return /** @type {?} */(( this.autocomplete._keyManager.activeItem as MdOption));
    }
  }
/**
 * Stream of clicks outside of the autocomplete panel.
 * @return {?}
 */
private get _outsideClickStream(): Observable<any> {
    if (this._document) {
      return Observable.fromEvent(this._document, 'click').filter((event: MouseEvent) => {
        const /** @type {?} */ clickTarget = /** @type {?} */(( event.target as HTMLElement));
        const /** @type {?} */ inputContainer = this._inputContainer ?
            this._inputContainer._elementRef.nativeElement : null;

        return this._panelOpen &&
               clickTarget !== this._element.nativeElement &&
               (!inputContainer || !inputContainer.contains(clickTarget)) &&
               !this._overlayRef.overlayElement.contains(clickTarget);
      });
    }
  }
/**
 * Sets the autocomplete's value. Part of the ControlValueAccessor interface
 * required to integrate with Angular's core forms API.
 * 
 * @param {?} value New value to be written to the model.
 * @return {?}
 */
writeValue(value: any): void {
    Promise.resolve(null).then(() => this._setTriggerValue(value));
  }
/**
 * Saves a callback function to be invoked when the autocomplete's value
 * changes from user input. Part of the ControlValueAccessor interface
 * required to integrate with Angular's core forms API.
 * 
 * @param {?} fn Callback to be triggered when the value changes.
 * @return {?}
 */
registerOnChange(fn: (value: any) => {}): void {
    this._onChange = fn;
  }
/**
 * Saves a callback function to be invoked when the autocomplete is blurred
 * by the user. Part of the ControlValueAccessor interface required
 * to integrate with Angular's core forms API.
 * 
 * @param {?} fn Callback to be triggered when the component has been touched.
 * @return {?}
 */
registerOnTouched(fn: () => {}) {
    this._onTouched = fn;
  }
/**
 * @param {?} event
 * @return {?}
 */
_handleKeydown(event: KeyboardEvent): void {
    if (event.keyCode === ESCAPE && this.panelOpen) {
      this.closePanel();
    } else if (this.activeOption && event.keyCode === ENTER) {
      this.activeOption._selectViaInteraction();
      event.preventDefault();
    } else {
      const /** @type {?} */ prevActiveItem = this.autocomplete._keyManager.activeItem;
      const /** @type {?} */ isArrowKey = event.keyCode === UP_ARROW || event.keyCode === DOWN_ARROW;

      this.autocomplete._keyManager.onKeydown(event);

      if (isArrowKey) {
        this.openPanel();
      }

      Promise.resolve().then(() => {
        if (isArrowKey || this.autocomplete._keyManager.activeItem !== prevActiveItem) {
          this._scrollToOption();
        }
      });
    }
  }
/**
 * @param {?} event
 * @return {?}
 */
_handleInput(event: KeyboardEvent): void {
    // We need to ensure that the input is focused, because IE will fire the `input`
    // event on focus/blur/load if the input has a placeholder. See:
    // https://connect.microsoft.com/IE/feedback/details/885747/
    if (document.activeElement === event.target) {
      this._onChange(( /** @type {?} */((event.target as HTMLInputElement))).value);
      this.openPanel();
    }
  }
/**
 * In "auto" mode, the placeholder will animate down as soon as focus is lost.
 * This causes the value to jump when selecting an option with the mouse.
 * This method manually floats the placeholder until the panel can be closed.
 * @return {?}
 */
private _floatPlaceholder(): void {
    if (this._inputContainer && this._inputContainer.floatPlaceholder === 'auto') {
      this._inputContainer.floatPlaceholder = 'always';
      this._manuallyFloatingPlaceholder = true;
    }
  }
/**
 * If the placeholder has been manually elevated, return it to its normal state.
 * @return {?}
 */
private _resetPlaceholder(): void  {
    if (this._manuallyFloatingPlaceholder) {
      this._inputContainer.floatPlaceholder = 'auto';
      this._manuallyFloatingPlaceholder = false;
    }
  }
/**
 * Given that we are not actually focusing active options, we must manually adjust scroll
 * to reveal options below the fold. First, we find the offset of the option from the top
 * of the panel. The new scrollTop will be that offset - the panel height + the option
 * height, so the active option will be just visible at the bottom of the panel.
 * @return {?}
 */
private _scrollToOption(): void {
    const /** @type {?} */ optionOffset =
        this.autocomplete._keyManager.activeItemIndex * AUTOCOMPLETE_OPTION_HEIGHT;
    const /** @type {?} */ newScrollTop =
        Math.max(0, optionOffset - AUTOCOMPLETE_PANEL_HEIGHT + AUTOCOMPLETE_OPTION_HEIGHT);
    this.autocomplete._setScrollTop(newScrollTop);
  }
/**
 * This method listens to a stream of panel closing actions and resets the
 * stream every time the option list changes.
 * @return {?}
 */
private _subscribeToClosingActions(): void {
    // When the zone is stable initially, and when the option list changes...
    Observable.merge(this._zone.onStable.first(), this.autocomplete.options.changes)
        // create a new stream of panelClosingActions, replacing any previous streams
        // that were created, and flatten it so our stream only emits closing events...
        .switchMap(() => {
          this._resetPanel();
          return this.panelClosingActions;
        })
        // when the first closing event occurs...
        .first()
        // set the value, close the panel, and complete.
        .subscribe(event => this._setValueAndClose(event));
  }
/**
 * Destroys the autocomplete suggestion panel.
 * @return {?}
 */
private _destroyPanel(): void {
    if (this._overlayRef) {
      this.closePanel();
      this._overlayRef.dispose();
      this._overlayRef = null;
    }
  }
/**
 * @param {?} value
 * @return {?}
 */
private _setTriggerValue(value: any): void {
    const /** @type {?} */ toDisplay = this.autocomplete.displayWith ? this.autocomplete.displayWith(value) : value;
    this._element.nativeElement.value = toDisplay || '';
  }
/**
 * This method closes the panel, and if a value is specified, also sets the associated
 * control to that value. It will also mark the control as dirty if this interaction
 * stemmed from the user.
 * @param {?} event
 * @return {?}
 */
private _setValueAndClose(event: MdOptionSelectionChange | null): void {
    if (event && event.source) {
      this._clearPreviousSelectedOption(event.source);
      this._setTriggerValue(event.source.value);
      this._onChange(event.source.value);
      this._element.nativeElement.focus();
    }

    this.closePanel();
  }
/**
 * Clear any previous selected option and emit a selection change event for this option
 * @param {?} skip
 * @return {?}
 */
private _clearPreviousSelectedOption(skip: MdOption) {
    this.autocomplete.options.forEach((option) => {
      if (option != skip && option.selected) {
        option.deselect();
      }
    });
  }
/**
 * @return {?}
 */
private _createOverlay(): void {
    this._portal = new TemplatePortal(this.autocomplete.template, this._viewContainerRef);
    this._overlayRef = this._overlay.create(this._getOverlayConfig());
  }
/**
 * @return {?}
 */
private _getOverlayConfig(): OverlayState {
    const /** @type {?} */ overlayState = new OverlayState();
    overlayState.positionStrategy = this._getOverlayPosition();
    overlayState.width = this._getHostWidth();
    overlayState.direction = this._dir ? this._dir.value : 'ltr';
    overlayState.scrollStrategy = this._overlay.scrollStrategies.reposition();
    return overlayState;
  }
/**
 * @return {?}
 */
private _getOverlayPosition(): PositionStrategy {
    this._positionStrategy =  this._overlay.position().connectedTo(
        this._element,
        {originX: 'start', originY: 'bottom'}, {overlayX: 'start', overlayY: 'top'})
        .withFallbackPosition(
            {originX: 'start', originY: 'top'}, {overlayX: 'start', overlayY: 'bottom'}
        );
    this._subscribeToPositionChanges(this._positionStrategy);
    return this._positionStrategy;
  }
/**
 * This method subscribes to position changes in the autocomplete panel, so the panel's
 * y-offset can be adjusted to match the new position.
 * @param {?} strategy
 * @return {?}
 */
private _subscribeToPositionChanges(strategy: ConnectedPositionStrategy) {
    this._panelPositionSubscription = strategy.onPositionChange.subscribe(change => {
      this.autocomplete.positionY = change.connectionPair.originY === 'top' ? 'above' : 'below';
    });
  }
/**
 * Returns the width of the input element, so the panel width can match it.
 * @return {?}
 */
private _getHostWidth(): number {
    return this._element.nativeElement.getBoundingClientRect().width;
  }
/**
 * Reset active item to null so arrow events will activate the correct options.
 * @return {?}
 */
private _resetActiveItem(): void {
    this.autocomplete._keyManager.setActiveItem(null);
  }
/**
 * Resets the active item and re-calculates alignment of the panel in case its size
 * has changed due to fewer or greater number of options.
 * @return {?}
 */
private _resetPanel() {
    this._resetActiveItem();
    this._positionStrategy.recalculateLastPosition();
    this.autocomplete._setVisibility();
  }

static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'input[mdAutocomplete], input[matAutocomplete],' +
  'textarea[mdAutocomplete], textarea[matAutocomplete]',
  host: {
    'role': 'combobox',
    'autocomplete': 'off',
    'aria-autocomplete': 'list',
    'aria-multiline': 'false',
    '[attr.aria-activedescendant]': 'activeOption?.id',
    '[attr.aria-expanded]': 'panelOpen.toString()',
    '[attr.aria-owns]': 'autocomplete?.id',
    '(focus)': 'openPanel()',
    '(input)': '_handleInput($event)',
    '(blur)': '_onTouched()',
    '(keydown)': '_handleKeydown($event)',
  },
  providers: [MD_AUTOCOMPLETE_VALUE_ACCESSOR]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: Overlay, },
{type: ViewContainerRef, },
{type: ChangeDetectorRef, },
{type: Dir, decorators: [{ type: Optional }, ]},
{type: NgZone, },
{type: MdInputContainer, decorators: [{ type: Optional }, { type: Host }, ]},
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [DOCUMENT, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'autocomplete': [{ type: Input, args: ['mdAutocomplete', ] },],
'_matAutocomplete': [{ type: Input, args: ['matAutocomplete', ] },],
};
}

function MdAutocompleteTrigger_tsickle_Closure_declarations() {
/** @type {?} */
MdAutocompleteTrigger.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdAutocompleteTrigger.ctorParameters;
/** @type {?} */
MdAutocompleteTrigger.propDecorators;
/** @type {?} */
MdAutocompleteTrigger.prototype._overlayRef;
/** @type {?} */
MdAutocompleteTrigger.prototype._portal;
/** @type {?} */
MdAutocompleteTrigger.prototype._panelOpen;
/**
 * The subscription to positioning changes in the autocomplete panel.
 * @type {?}
 */
MdAutocompleteTrigger.prototype._panelPositionSubscription;
/**
 * Strategy that is used to position the panel.
 * @type {?}
 */
MdAutocompleteTrigger.prototype._positionStrategy;
/**
 * Whether or not the placeholder state is being overridden.
 * @type {?}
 */
MdAutocompleteTrigger.prototype._manuallyFloatingPlaceholder;
/**
 * View -> model callback called when value changes
 * @type {?}
 */
MdAutocompleteTrigger.prototype._onChange;
/**
 * View -> model callback called when autocomplete has been touched
 * @type {?}
 */
MdAutocompleteTrigger.prototype._onTouched;
/** @type {?} */
MdAutocompleteTrigger.prototype.autocomplete;
/** @type {?} */
MdAutocompleteTrigger.prototype._element;
/** @type {?} */
MdAutocompleteTrigger.prototype._overlay;
/** @type {?} */
MdAutocompleteTrigger.prototype._viewContainerRef;
/** @type {?} */
MdAutocompleteTrigger.prototype._changeDetectorRef;
/** @type {?} */
MdAutocompleteTrigger.prototype._dir;
/** @type {?} */
MdAutocompleteTrigger.prototype._zone;
/** @type {?} */
MdAutocompleteTrigger.prototype._inputContainer;
/** @type {?} */
MdAutocompleteTrigger.prototype._document;
}



interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
